import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { bookingApi } from "@/api";
import type { BookingInfo, BookingSlot } from "@/types";

export default function BookingPage() {
  const { code } = useParams<{ code: string }>();
  const params = new URLSearchParams(window.location.search);
  const token = code || params.get("t") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [booking, setBooking] = useState<BookingInfo | null>(null);
  const [slots, setSlots] = useState<BookingSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [phone, setPhone] = useState("");
  const [editingPhone, setEditingPhone] = useState(false);
  const [customerName, setCustomerName] = useState(""); // 表示用（API確定時は更新しない）

  useEffect(() => {
    if (!token) {
      setError("予約リンクが無効です");
      setLoading(false);
      return;
    }
    bookingApi
      .info(token)
      .then((res) => {
        setBooking(res.booking);
        setSlots(res.availableSlots);
        if (res.availableSlots.length > 0) {
          setSelectedDate(res.availableSlots[0].date);
        }
        if (res.booking.traderPhone) {
          setPhone(res.booking.traderPhone.startsWith("+81") ? "0" + res.booking.traderPhone.slice(3) : res.booking.traderPhone);
        }
        setCustomerName(res.booking.traderName || "");
        if (res.booking.status === "CONFIRMED") {
          setConfirmed(true);
        }
      })
      .catch((err) => {
        if (err?.status === 410) {
          setError("この予約リンクは有効期限が切れています");
        } else if (err?.status === 404) {
          setError("予約リンクが見つかりません");
        } else {
          setError("読み込みに失敗しました");
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleConfirm = async () => {
    if (!selectedDate || !selectedTime || !booking) return;
    setConfirming(true);
    try {
      const res = await bookingApi.confirm({
        bookingId: booking.id,
        slotDate: selectedDate,
        slotTime: selectedTime,
        traderPhone: phone || undefined,
      });
      setBooking(res.booking);
      setConfirmed(true);
    } catch (err: any) {
      if (err?.status === 409) {
        setError("選択した時間枠は既に予約済みです。別の時間をお選びください。");
      } else {
        setError("予約の確定に失敗しました。もう一度お試しください。");
      }
    } finally {
      setConfirming(false);
    }
  };

  const currentSlots = slots.find((s) => s.date === selectedDate)?.slots || [];

  // +81... → 0... に変換して表示
  const formatPhone = (p: string) =>
    p.startsWith("+81") ? "0" + p.slice(3) : p;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + "T00:00:00+09:00");
    const days = ["日", "月", "火", "水", "木", "金", "土"];
    return `${d.getMonth() + 1}/${d.getDate()} (${days[d.getDay()]})`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">読み込み中...</p>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-sm border max-w-md text-center">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  // 確定済み表示
  if (confirmed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-sm border max-w-md w-full text-center">
          <p className="text-xs text-slate-400 mb-3">SphereLight（スフィアライト）</p>
          <div className="text-4xl mb-4">&#10003;</div>
          <h1 className="text-lg font-bold mb-2">ご予約を承りました</h1>
          <p className="text-sm text-slate-600 mb-4">
            {booking.companyName && `${booking.companyName} `}{customerName || booking.traderName} 様
          </p>
          <div className="bg-slate-50 rounded-lg p-4 text-sm">
            <div className="flex justify-between py-1">
              <span className="text-slate-500">日時</span>
              <span className="font-medium">
                {booking.slotDate && formatDate(booking.slotDate)} {booking.slotTime}
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-slate-500">担当</span>
              <span className="font-medium">{booking.staffName}</span>
            </div>
            {phone && (
              <div className="flex justify-between py-1">
                <span className="text-slate-500">折り返し先</span>
                <span className="font-medium">{formatPhone(phone)}</span>
              </div>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-4">
            上記の番号に担当者よりご連絡いたします
          </p>
          <p className="text-xs text-slate-400 mt-1">
            予約時間の変更はSMSにてご連絡ください
          </p>
        </div>
      </div>
    );
  }

  // 予約選択画面
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white p-6 rounded-lg shadow-sm border max-w-md w-full">
        <p className="text-xs text-slate-400 mb-3">SphereLight（スフィアライト）</p>
        <h1 className="text-lg font-bold mb-1">お電話のご予約</h1>
        {customerName ? (
          <p className="text-sm text-slate-600 mb-1">
            {booking.companyName && `${booking.companyName} `}{customerName} 様
          </p>
        ) : null}
        <p className="text-sm text-slate-600 mb-4">
          ご都合の良い日時をお選びください。担当の{booking.staffName}よりお電話いたします。
        </p>

        {/* 折り返し先電話番号 */}
        <div className="mb-4 bg-slate-50 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">折り返し先</span>
            {!editingPhone && (
              <button
                onClick={() => setEditingPhone(true)}
                className="text-xs text-blue-500 hover:underline"
              >
                変更
              </button>
            )}
          </div>
          {editingPhone ? (
            <div className="flex items-center gap-2 mt-1">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="電話番号を入力"
                className="flex-1 px-3 py-1.5 border rounded-md text-sm"
                autoFocus
              />
              <button
                onClick={() => setEditingPhone(false)}
                className="px-3 py-1.5 bg-blue-500 text-white rounded-md text-xs"
              >
                OK
              </button>
            </div>
          ) : (
            <p className="text-sm font-medium mt-0.5">
              {phone ? formatPhone(phone) : <span className="text-slate-400">未設定</span>}
            </p>
          )}
        </div>

        {error && (
          <p className="text-xs text-red-600 mb-3 bg-red-50 p-2 rounded">{error}</p>
        )}

        {/* 日付タブ */}
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {slots.map((s) => (
            <button
              key={s.date}
              onClick={() => { setSelectedDate(s.date); setSelectedTime(""); setError(""); }}
              className={`px-3 py-2 rounded-md text-sm whitespace-nowrap border ${
                selectedDate === s.date
                  ? "bg-blue-500 text-white border-blue-500"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {formatDate(s.date)}
            </button>
          ))}
        </div>

        {/* 時間枠グリッド（午前/午後で分割） */}
        {currentSlots.length > 0 ? (
          <div className="mb-4 space-y-3">
            {(() => {
              const am = currentSlots.filter((t) => parseInt(t) < 12);
              const pm = currentSlots.filter((t) => parseInt(t) >= 12);
              const renderGroup = (label: string, times: string[]) =>
                times.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 mb-1.5">{label}</p>
                    <div className="grid grid-cols-4 gap-1.5">
                      {times.map((time) => (
                        <button
                          key={time}
                          onClick={() => { setSelectedTime(time); setError(""); }}
                          className={`py-1.5 rounded text-xs border ${
                            selectedTime === time
                              ? "bg-blue-500 text-white border-blue-500"
                              : "bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              return (
                <>
                  {renderGroup("午前", am)}
                  {renderGroup("午後", pm)}
                </>
              );
            })()}
          </div>
        ) : (
          <p className="text-sm text-slate-400 mb-4">この日は空きがありません</p>
        )}

        {/* 確定ボタン */}
        <button
          onClick={handleConfirm}
          disabled={!selectedDate || !selectedTime || confirming}
          className="w-full py-3 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
        >
          {confirming
            ? "確定中..."
            : selectedTime
              ? `${formatDate(selectedDate)} ${selectedTime} で予約する`
              : "時間を選択してください"}
        </button>
      </div>
    </div>
  );
}
